import React, { PropsWithChildren } from 'react';
import cn from "classnames";
import {useTranslation} from 'react-i18next';
import {FieldError, RegisterOptions, UseFormMethods} from 'react-hook-form';

import './form.scss';

interface IFormInputProps<FormValues> {
    autocomplete?: string,
    dataCy?: string;
    defaultValue?: string | number;
    disabled?: boolean;
    form: UseFormMethods<FormValues>
    label: string;
    maxLength?: number;
    name: keyof FormValues & string;
    registerOptions?: RegisterOptions;
    type?: 'text' | 'number' | 'submit' | 'file';
}

export function FormInput<ObjectType>(props: PropsWithChildren<IFormInputProps<ObjectType>>) {
    const {t, i18n} = useTranslation();
    const {autocomplete, defaultValue, disabled, form, label, maxLength, name, registerOptions, type = 'text'} = props;

    const error = form.errors[name] as FieldError;
    let errorMessage = error?.message;
    // If we weren't passed a custom message for this error, use a generic one for its type.
    if (error && !errorMessage) {
        switch(error.type) {
            case 'required':
                errorMessage = t('errors.validation.required');
                break;
            default:
                errorMessage = t('errors.validation.fallback', {errorType: error.type});
                break;
        }
    }

    return (
        <div className={cn({
            formItem: true,
            error: !!error,
        })}>
            <div className="formItem_label">
                {label}
            </div>
            <input
                autoComplete={autocomplete}
                className="formItem_input"
                defaultValue={defaultValue}
                disabled={disabled}
                maxLength={maxLength}
                name={name}
                ref={form.register(registerOptions)}
                type={type}
                data-cy={props.dataCy}
            />
            {error && (
                <div className="formItem_error" data-cy={props.dataCy + "_error"}>
                    {errorMessage}
                </div>
            )}
        </div>
    );
};
